"use client";

import Link from "next/link";
import { useRef, useState, type FormEvent, type Ref } from "react";
import { EMAIL_FORMAT } from "@shared/formats/email";
import { PHONE_FORMAT } from "@shared/formats/phone";
import { Lock } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import type {
  GuestCheckoutContact,
  GuestContactPrompt,
} from "@/hooks/checkout/use-guest-contact-prompt";

interface ContactErrors {
  email?: string;
  phone?: string;
}

function validateContact(email: string, phone: string): ContactErrors {
  return {
    email: emailError(email),
    phone:
      phone.trim() === "" || PHONE_FORMAT.validate(phone)
        ? undefined
        : (PHONE_FORMAT.diagnose(phone) ?? "Enter a 10-digit phone number"),
  };
}

function emailError(email: string): string | undefined {
  if (email.trim() === "") return "Enter your email";
  return EMAIL_FORMAT.validate(email) ? undefined : EMAIL_FORMAT.errorMessage;
}

interface ContactDialogProps {
  prompt: GuestContactPrompt;
  totalLabel: string | null;
  returnTo: string;
}

export function GuestCheckoutContactDialog({
  prompt,
  totalLabel,
  returnTo,
}: ContactDialogProps) {
  return (
    <Dialog
      open={prompt.open}
      onOpenChange={(open) => {
        if (!open) prompt.onDismiss();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Your contact details</DialogTitle>
          <DialogDescription>
            We send your receipt and delivery updates here.
          </DialogDescription>
        </DialogHeader>
        {prompt.open ? (
          <ContactForm
            initial={prompt.initial}
            onSubmit={prompt.onSubmit}
            totalLabel={totalLabel}
            returnTo={returnTo}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function useContactForm(
  initial: GuestCheckoutContact,
  onSubmit: (contact: GuestCheckoutContact) => void,
) {
  const [email, setEmail] = useState(initial.email);
  const [phone, setPhone] = useState(PHONE_FORMAT.format(initial.phone ?? ""));
  const [errors, setErrors] = useState<ContactErrors>({});
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const submit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const found = validateContact(email, phone);
    setErrors(found);
    if (found.email || found.phone) {
      (found.email ? emailRef : phoneRef).current?.focus();
      return;
    }
    onSubmit({
      email: email.trim(),
      phone: PHONE_FORMAT.normalize(phone),
    });
  };
  return {
    email,
    setEmail,
    phone,
    setPhone,
    errors,
    emailRef,
    phoneRef,
    submit,
  };
}

export type ContactFormState = ReturnType<typeof useContactForm>;

function ContactForm({
  initial,
  onSubmit,
  totalLabel,
  returnTo,
}: {
  initial: GuestCheckoutContact;
  onSubmit: (contact: GuestCheckoutContact) => void;
  totalLabel: string | null;
  returnTo: string;
}) {
  const form = useContactForm(initial, onSubmit);

  return (
    <form noValidate onSubmit={form.submit} className="space-y-4">
      <ContactFields form={form} autoFocusEmail />
      <ContactFooter totalLabel={totalLabel} returnTo={returnTo} />
    </form>
  );
}

export function ContactFields({
  form,
  autoFocusEmail = false,
}: {
  form: ContactFormState;
  autoFocusEmail?: boolean;
}) {
  return (
    <>
      <ContactInput
        label="Email"
        required
        type="email"
        autoFocus={autoFocusEmail}
        inputRef={form.emailRef}
        value={form.email}
        error={form.errors.email}
        onChange={form.setEmail}
      />
      <ContactInput
        label="Phone"
        type="tel"
        inputRef={form.phoneRef}
        value={form.phone}
        error={form.errors.phone}
        onChange={(value) => form.setPhone(PHONE_FORMAT.format(value))}
      />
    </>
  );
}

function ContactFooter({
  totalLabel,
  returnTo,
}: {
  totalLabel: string | null;
  returnTo: string;
}) {
  return (
    <DialogFooter className="flex-col gap-3 pt-2 sm:flex-col">
      <Button type="submit" variant="brand" className="w-full font-semibold">
        <Lock className="size-4" />
        {totalLabel ? `Continue to pay ${totalLabel}` : "Continue to payment"}
      </Button>
      <SignInPrompt returnTo={returnTo} />
    </DialogFooter>
  );
}

export function SignInPrompt({ returnTo }: { returnTo: string }) {
  return (
    <p className="text-center text-sm text-muted-foreground">
      Have an account?{" "}
      <Link
        href={`/sign-in?${new URLSearchParams({ returnTo }).toString()}`}
        className="text-link"
      >
        Sign in
      </Link>
    </p>
  );
}

function ContactInput({
  label,
  required = false,
  type,
  autoFocus = false,
  inputRef,
  value,
  error,
  onChange,
}: {
  label: string;
  required?: boolean;
  type: "email" | "tel";
  autoFocus?: boolean;
  inputRef: Ref<HTMLInputElement>;
  value: string;
  error: string | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <Field>
      <FieldLabel required={required} optional={!required}>
        {label}
      </FieldLabel>
      <Input
        type={type}
        autoComplete={type}
        inputMode={type}
        ref={inputRef}
        autoFocus={autoFocus}
        placeholder={type === "tel" ? PHONE_FORMAT.display : "you@company.com"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldError>{error}</FieldError>
    </Field>
  );
}
